/* ==========================================================================
   FILE: pages/HomePage.jsx
   IAMONEAI - Home/Landing Page
   Public landing page for non-authenticated users
   ========================================================================== */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../global/components';
import './HomePage.css';

// ==========================================================================
// HOME PAGE COMPONENT
// ==========================================================================

function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="home-header-content">
          <div className="home-logo">
            <BrainIcon />
            <span>IAMONEAI</span>
          </div>
          <nav className="home-nav">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/auth?mode=login')}
            >
              Sign In
            </Button>
            <Button 
              variant="primary"
              onClick={() => navigate('/auth?mode=register')}
            >
              Get Started
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            Your Brain.<br />
            Your Rules.<br />
            <span className="home-hero-highlight">Your Guardian.</span>
          </h1>
          <p className="home-hero-subtitle">
            IAMONEAI is your personal AI guardian that remembers everything, 
            respects your boundaries, and grows with you. Take control of 
            your AI experience.
          </p>
          <div className="home-hero-actions">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => navigate('/auth?mode=register')}
            >
              Create Your Identity
              <ArrowRightIcon />
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => navigate('/auth?mode=login')}
            >
              Sign In
            </Button>
          </div>
          <div className="home-hero-trust">
            <ShieldIcon />
            <span>Blockchain-verified identity • Your data, your control</span>
          </div>
        </div>
        <div className="home-hero-visual">
          <div className="home-hero-brain">
            <div className="brain-orb">
              <div className="brain-pulse" />
              <div className="brain-icon">🧠</div>
            </div>
            <div className="brain-connections">
              <div className="connection connection-1">💭</div>
              <div className="connection connection-2">🎯</div>
              <div className="connection connection-3">🛡️</div>
              <div className="connection connection-4">💾</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features">
        <div className="home-features-header">
          <h2>What Makes IAMONEAI Different</h2>
          <p>Not another chatbot. Your cognitive layer for the AI world.</p>
        </div>

        <div className="home-features-grid">
          <FeatureCard
            icon="🧠"
            title="Semantic Memory"
            description="I remember everything you share. Facts, preferences, beliefs — all organized and searchable. Your digital memory that grows smarter over time."
          />
          <FeatureCard
            icon="🛡️"
            title="Your Guardrails"
            description="You set the boundaries. Block topics you don't want help with. I enforce YOUR rules, never my own judgment. Complete control."
          />
          <FeatureCard
            icon="🎯"
            title="Goal Tracking"
            description="Share your goals and I'll help you track them. Deadlines, check-ins, and gentle reminders to keep you on track."
          />
          <FeatureCard
            icon="🔄"
            title="Context Switching"
            description="Work mode. Personal mode. Family mode. Switch contexts and I adapt my tone, style, and what I remember."
          />
          <FeatureCard
            icon="⛓️"
            title="Blockchain Identity"
            description="Your identity is anchored on the blockchain. Immutable proof that YOU are YOU. No one can alter your digital self."
          />
          <FeatureCard
            icon="🎭"
            title="Personality Kernel"
            description="Customize how I communicate. Formal or casual? Direct or gentle? Adjust 5 personality axes to match your preferences."
          />
        </div>
      </section>

      {/* Two Laws Section */}
      <section className="home-laws">
        <div className="home-laws-content">
          <h2>The Two Laws</h2>
          <p className="home-laws-subtitle">
            These are the immutable principles that guide IAMONEAI
          </p>

          <div className="home-laws-grid">
            <div className="law-card law-card-1">
              <span className="law-number">01</span>
              <h3>You Are The Owner</h3>
              <p>
                You decide what IAMONEAI can and cannot do. You set the boundaries, 
                not the AI. Your values are never overridden.
              </p>
            </div>
            <div className="law-card law-card-2">
              <span className="law-number">02</span>
              <h3>I Protect You</h3>
              <p>
                I block actions YOU marked as restricted. I warn you about potential harm. 
                I guard your identity from manipulation. I enforce YOUR rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div className="home-cta-content">
          <h2>Ready to Take Control?</h2>
          <p>
            Join the future where AI serves you, not the other way around.
            Create your blockchain-verified identity in minutes.
          </p>
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => navigate('/auth?mode=register')}
          >
            Create Your Identity
            <ArrowRightIcon />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-footer-content">
          <div className="home-footer-brand">
            <div className="home-logo">
              <BrainIcon />
              <span>IAMONEAI</span>
            </div>
            <p>Your brain. Your rules. Your guardian.</p>
          </div>
          <div className="home-footer-links">
            <div className="footer-link-group">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#security">Security</a>
              <a href="#roadmap">Roadmap</a>
            </div>
            <div className="footer-link-group">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#blog">Blog</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="footer-link-group">
              <h4>Legal</h4>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
            </div>
          </div>
        </div>
        <div className="home-footer-bottom">
          <p>© 2025 IAMONEAI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ==========================================================================
// FEATURE CARD COMPONENT
// ==========================================================================

function FeatureCard({ icon, title, description }) {
  return (
    <div className="feature-card">
      <span className="feature-card-icon">{icon}</span>
      <h3 className="feature-card-title">{title}</h3>
      <p className="feature-card-description">{description}</p>
    </div>
  );
}

// ==========================================================================
// ICONS
// ==========================================================================

function BrainIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default HomePage;